/** Sample data, for looking at screens before the backend exists.
 *
 * Active only when no API URL is configured — which is true in `npm run dev`
 * and false in every deployed environment, because the pipeline writes
 * `window.FKMT_CONFIG.apiUrl` at deploy time.
 *
 * UI_TASTE.md: sample data must be labelled explicitly so it is never
 * mistaken for live data. The banner in App.tsx does that, and this module
 * cannot load at all once a real API is configured.
 *
 * It deliberately mimics two server behaviours that shape the UI, so what
 * you see here is what you will see for real:
 *
 *   - `values_visible: false` omits the commercial columns **from the rows
 *     entirely**, exactly as the API does for a caller without
 *     `view_values`. Toggle MASKED below to see a storekeeper's view.
 *   - a stale `version` on save is refused with a 409, so the concurrent-
 *     edit message can be seen without two people and a race.
 */

/** Flip to true to see the screens as a user without `view_values` — the
 *  most-used right in the legacy system (LEGACY_ANALYSIS.md §6.1). */
const MASKED = false

type Row = Record<string, unknown>

const COMPANIES: Row[] = [
  { company_id: 'FKMT', name: 'FKMT LLP', legal_name: 'FKMT LLP',
    gstin: '27AAFFF1234A1Z5', address: 'Pune', status: 'Active', version: 1 },
  { company_id: 'GHIN', name: 'Goldhofer India', legal_name: 'Goldhofer India Pvt Ltd',
    gstin: '27AAACG5678B1Z2', address: 'Pune', status: 'Active', version: 1 },
]

const USERS: Row[] = [
  { user_id: 'anil', username: 'anil', name: 'Anil Agrawal',
    designation: 'Director', email: 'admin@fkmt.in', status: 'Active', version: 1 },
  { user_id: 'sandeep', username: 'sandeep', name: 'Sandeep Kulkarni',
    designation: 'Purchase Head', email: 'sandeep@fkmt.in', status: 'Active', version: 2 },
  { user_id: 'nitin', username: 'nitin', name: 'Nitin Deshpande',
    designation: 'Stores In-charge', email: 'nitin@fkmt.in', status: 'Active', version: 1 },
  { user_id: 'prashant', username: 'prashant', name: 'Prashant Achari',
    designation: 'Design Engineer', email: 'prashant@fkmt.in', status: 'Inactive', version: 3 },
]

const ROLES: Row[] = [
  { role_id: 'admin', name: 'Administrator',
    description: 'Full access. Held by named individuals, never shared.', status: 'Active', version: 1 },
  { role_id: 'purchase', name: 'Purchase',
    description: 'Raises and receives purchase orders', status: 'Active', version: 1 },
  { role_id: 'stores', name: 'Stores',
    description: 'Issues and receives material. No commercial values.', status: 'Active', version: 1 },
]

const ITEMS: Row[] = [
  { item_id: 'I1', item_code: 'HYD-CYL-100', item_name: 'Hydraulic Cylinder 100mm',
    uom: 'NOS', purchase_rate: 18500, standard_cost: 19200, status: 'Active', version: 1 },
  { item_id: 'I2', item_code: 'AXL-BRG-45', item_name: 'Axle Bearing 45mm',
    uom: 'NOS', purchase_rate: 3250, standard_cost: 3400, status: 'Active', version: 1 },
  { item_id: 'I3', item_code: 'STL-PLT-12', item_name: 'Steel Plate 12mm IS2062',
    uom: 'KG', purchase_rate: 68.5, standard_cost: 71, status: 'Active', version: 4 },
  { item_id: 'I4', item_code: 'PNT-PU-GRY', item_name: 'PU Paint — Grey RAL7016',
    uom: 'LTR', purchase_rate: 640, standard_cost: 665, status: 'Active', version: 1 },
  { item_id: 'I5', item_code: 'TYR-1200R20', item_name: 'Tyre 1200R20 (superseded)',
    uom: 'NOS', purchase_rate: 24000, standard_cost: 24800, status: 'Inactive', version: 2 },
]

const PO_HEADERS: Row[] = [
  { po_id: 'PO1', doc_no: 'PO-0041', doc_date: '2026-08-14', supplier_id: 'Bharat Forge',
    project_id: 'PRJ-2026-03', remarks: '', total_value: 240500, status: 'Authorised', version: 3 },
  { po_id: 'PO2', doc_no: 'PO-0042', doc_date: '2026-08-19', supplier_id: 'Tata Steel',
    project_id: 'PRJ-2026-03', remarks: 'Urgent', total_value: 68500, status: 'Submitted', version: 1 },
  { po_id: 'PO3', doc_no: null, doc_date: '2026-08-22', supplier_id: 'Asian Paints',
    project_id: '', remarks: '', total_value: 12800, status: 'Draft', version: 1 },
]

const PO_LINES: Record<string, Row[]> = {
  PO1: [
    { line_no: 1, item_id: 'HYD-CYL-100', uom: 'NOS', qty: 12, rate: 18500, amount: 222000 },
    { line_no: 2, item_id: 'AXL-BRG-45',  uom: 'NOS', qty: 5,  rate: 3250,  amount: 16250 },
    { line_no: 3, item_id: 'PNT-PU-GRY',  uom: 'LTR', qty: 3.5, rate: 640,  amount: 2240 },
  ],
  PO2: [{ line_no: 1, item_id: 'STL-PLT-12', uom: 'KG', qty: 1000, rate: 68.5, amount: 68500 }],
  PO3: [{ line_no: 1, item_id: 'PNT-PU-GRY', uom: 'LTR', qty: 20, rate: 640, amount: 12800 }],
}

const TABLES: Record<string, { rows: Row[]; valueColumns: string[] }> = {
  'purchase/po':     { rows: PO_HEADERS, valueColumns: ['total_value'] },
  'admin/companies': { rows: COMPANIES, valueColumns: [] },
  'admin/users':     { rows: USERS,     valueColumns: [] },
  'admin/roles':     { rows: ROLES,     valueColumns: [] },
  'masters/items':   { rows: ITEMS,     valueColumns: ['purchase_rate', 'standard_cost'] },
}

function strip(row: Row, valueColumns: string[]): Row {
  // The server does not send what the caller may not see. Neither does this.
  const out = { ...row }
  for (const c of valueColumns) delete out[c]
  return out
}

export function isMockMode(): boolean {
  return !window.FKMT_CONFIG?.apiUrl
}

const MOCK_COMPANIES = [
  { company_id: 'FKMT', name: 'FKMT LLP', years: [
      { financial_year: '2026-27', status: 'Open', is_current: true },
      { financial_year: '2025-26', status: 'Closed', is_current: false }] },
  { company_id: 'GHIN', name: 'Goldhofer India', years: [
      { financial_year: '2026-27', status: 'Open', is_current: true }] },
]

async function mockAuth(action: string, body?: Record<string, unknown>) {
  if (action === 'login') {
    const u = String(body?.username ?? '').toLowerCase()
    const p = String(body?.password ?? '')
    // Any password works except the word 'wrong', so the failure path can
    // be seen too. Unknown user and wrong password give the same answer.
    if (!u || p === 'wrong') {
      const err = new Error('Invalid username or password.')
      ;(err as Error & { status?: number }).status = 401
      throw err
    }
    return {
      access_token: 'sample-token',
      user: { user_id: u, name: u === 'anil' ? 'Anil Agrawal' : 'Sandeep Kulkarni',
              username: u },
      companies: MOCK_COMPANIES,   // two, so the chooser step is exercised
    }
  }
  if (action === 'session' || action === 'switch') {
    return { session: { company_id: body?.company_id,
                        financial_year: body?.financial_year,
                        expires_at: Date.now() + 8 * 3600 * 1000 } }
  }
  if (action === 'logout') return { status: 'signed out' }
  throw new Error(`No sample handler for /auth/${action}`)
}

const PENDING = [
  { doc_id: 'PO2', doc_no: 'PO-0042', doc_date: '2026-08-19', doc_type: 'PO',
    title: 'Purchase Order', party: 'Tata Steel', value: 68500,
    created_by: 'sandeep', version: 1, age_days: 3,
    can_approve: true, blocked_reason: null },
  { doc_id: 'SO7', doc_no: 'SO-0107', doc_date: '2026-08-10', doc_type: 'SO',
    title: 'Sales Order', party: 'Bharat Forge', value: 1450000,
    created_by: 'nitin', version: 2, age_days: 12,
    can_approve: true, blocked_reason: null },
  { doc_id: 'PO9', doc_no: 'PO-0044', doc_date: '2026-08-21', doc_type: 'PO',
    title: 'Purchase Order', party: 'Larsen & Toubro', value: 4800000,
    created_by: 'sandeep', version: 1, age_days: 1,
    can_approve: false, blocked_reason: 'Above your approval limit' },
  { doc_id: 'PO8', doc_no: 'PO-0043', doc_date: '2026-08-21', doc_type: 'PO',
    title: 'Purchase Order', party: 'Asian Paints', value: 12800,
    created_by: 'anil', version: 1, age_days: 1,
    can_approve: false, blocked_reason: 'You raised this document' },
]

const APPROVAL_HISTORY: Record<string, Row[]> = {
  SO7: [{ level: 1, decision: 'Returned', decided_at: '2026-08-12',
          decided_by: 'Anil Agrawal', remarks: 'Delivery terms missing' }],
}

async function mockApprovals(parts: string[], method: string,
                             body?: Record<string, unknown>) {
  if (method === 'GET' && parts.length === 0) return { rows: PENDING }
  if (method === 'GET' && parts[2] === 'history') {
    return { rows: APPROVAL_HISTORY[parts[1]] ?? [] }
  }
  if (method === 'POST') {
    const i = PENDING.findIndex(p => p.doc_id === parts[1])
    if (i >= 0) PENDING.splice(i, 1)
    return { decision: body?.decision, status: 'Authorised' }
  }
  throw new Error('No sample handler for that approval route.')
}

const CATALOGUE = {
  actions: ['view', 'add', 'modify', 'delete', 'print', 'approve', 'view_values'],
  objects: [
    { module: 'masters', object: 'item', permissions: {
        view: 'masters.item.view', add: 'masters.item.add',
        modify: 'masters.item.modify', delete: 'masters.item.delete',
        print: 'masters.item.print', view_values: 'masters.item.view_values' } },
    { module: 'masters', object: 'supplier', permissions: {
        view: 'masters.supplier.view', add: 'masters.supplier.add',
        modify: 'masters.supplier.modify', print: 'masters.supplier.print',
        view_values: 'masters.supplier.view_values' } },
    { module: 'purchase', object: 'po', permissions: {
        view: 'purchase.po.view', add: 'purchase.po.add',
        modify: 'purchase.po.modify', delete: 'purchase.po.delete',
        print: 'purchase.po.print', approve: 'purchase.po.approve',
        view_values: 'purchase.po.view_values' } },
    { module: 'purchase', object: 'grn', permissions: {
        view: 'purchase.grn.view', add: 'purchase.grn.add',
        modify: 'purchase.grn.modify', print: 'purchase.grn.print' } },
    { module: 'stores', object: 'stock_balance', permissions: {
        view: 'stores.stock_balance.view', print: 'stores.stock_balance.print',
        view_values: 'stores.stock_balance.view_values' } },
    { module: 'admin', object: 'role', permissions: {
        view: 'admin.role.view', add: 'admin.role.add',
        modify: 'admin.role.modify', delete: 'admin.role.delete' } },
  ],
}

const ROLE_PERMS: Record<string, string[]> = {
  admin:    CATALOGUE.objects.flatMap(o => Object.values(o.permissions)),
  purchase: ['purchase.po.view', 'purchase.po.add', 'purchase.po.modify',
             'purchase.po.print', 'purchase.po.view_values',
             'purchase.grn.view', 'masters.supplier.view'],
  // A storekeeper: sees items and stock, never the money on them.
  stores:   ['masters.item.view', 'purchase.grn.view', 'purchase.grn.add',
             'stores.stock_balance.view', 'stores.stock_balance.print'],
}

const USER_ROLES: Record<string, string[]> = {
  anil: ['admin'], sandeep: ['purchase'], nitin: ['stores'], prashant: [],
}

const USER_AUTHORITY: Record<string, Row[]> = {
  anil: [{ doc_type: 'PO', level: 1, min_value: null, max_value: null,
           currency: 'INR' }],
  sandeep: [{ doc_type: 'PO', level: 1, min_value: 0, max_value: 500000,
              currency: 'INR' }],
}

async function mockAdminRights(parts: string[], method: string,
                               body?: Record<string, unknown>) {
  const [screen, id, tail] = parts
  if (screen === 'permissions') return CATALOGUE
  if (screen === 'roles' && tail === 'permissions') {
    if (method === 'PUT') {
      ROLE_PERMS[id] = (body?.permission_ids as string[]) ?? []
      return { added: [], removed: [] }
    }
    return { role: { role_id: id, name: id }, held: ROLE_PERMS[id] ?? [] }
  }
  if (screen === 'users' && tail === 'rights') {
    const held = USER_ROLES[id] ?? []
    const effective = new Set<string>()
    for (const r of held) for (const p of ROLE_PERMS[r] ?? []) effective.add(p)
    return {
      roles: Object.keys(ROLE_PERMS).map(r => ({
        role_id: r, name: r[0].toUpperCase() + r.slice(1), held: held.includes(r) })),
      overrides: [], authority: USER_AUTHORITY[id] ?? [],
      effective: [...effective].sort(),
    }
  }
  if (screen === 'users' && tail === 'roles' && method === 'PUT') {
    const next = (body?.role_ids as string[]) ?? []
    // The lockout guard, mimicked — so the message can be seen.
    if (id === 'anil' && !next.includes('admin')) {
      const err = new Error('This would leave nobody able to administer ' +
        'rights in this company. Grant the administrator role to someone ' +
        'else first.')
      ;(err as Error & { status?: number }).status = 400
      throw err
    }
    USER_ROLES[id] = next
    return { roles: next }
  }
  throw new Error('No sample handler for that rights route.')
}

export async function mockRequest(
  method: string, path: string, body?: Record<string, unknown>,
  query?: Record<string, string | undefined>,
): Promise<unknown> {
  await new Promise(r => setTimeout(r, 180))    // a visible, honest latency

  const [, ...rest] = path.split('/')
  const screen = rest.slice(0, 2).join('/')
  const id = rest[2]
  if (rest[0] === 'auth') return mockAuth(rest[1] ?? '', body)
  if (rest[0] === 'approvals') return mockApprovals(rest.slice(1), method, body)
  if (rest[0] === 'admin' && (rest[1] === 'permissions' || rest[3])) {
    return mockAdminRights(rest.slice(1), method, body)
  }

  const table = TABLES[screen]
  if (!table) throw new Error(`No sample data for /${screen}`)

  // A document opened by id returns header + lines, in two queries, never
  // one per line — archetypes/document.py get_document().
  if (method === 'GET' && id && screen === 'purchase/po') {
    const header = PO_HEADERS.find(h => h.po_id === id)
    if (!header) throw new Error('No such document.')
    const lines = (PO_LINES[id] ?? []).map(l =>
      MASKED ? strip(l, ['rate', 'amount']) : l)
    return { header: MASKED ? strip(header, ['total_value']) : header,
             lines, values_visible: !MASKED }
  }

  if (method === 'GET' && id === 'export') {
    const cols = Object.keys(table.rows[0] ?? {})
      .filter(k => !k.endsWith('version') &&
                   !(MASKED && table.valueColumns.includes(k)))
    const esc = (v: unknown) => {
      const t = v == null ? '' : String(v)
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t
    }
    const lines = [
      screen, 'Company: FKMT LLP,Financial year: 2026-27,Sample data', '',
      cols.join(','),
      ...table.rows.map(r => cols.map(c => esc(r[c])).join(',')),
      '', `${table.rows.length} row(s)`,
    ]
    return { filename: `${screen.replace('/', '-')}-sample.csv`,
             content_type: 'text/csv; charset=utf-8',
             body: '﻿' + lines.join('\n') }
  }

  if (method === 'GET') {
    let rows = table.rows
    if (query?.status) rows = rows.filter(r => r.status === query.status)
    return {
      rows: rows.map(r => MASKED ? strip(r, table.valueColumns) : r),
      count: rows.length,
      values_visible: !MASKED,
    }
  }

  if (method === 'POST') {
    // A document starts as Draft and unnumbered — a draft burns no document
    // number (archetypes/document.py). A master starts Active.
    const isDocument = screen === 'purchase/po'
    const row: Row = isDocument
      ? { po_id: `PO${PO_HEADERS.length + 1}`, doc_no: null,
          ...(body?.header as Row ?? {}), status: 'Draft', version: 1,
          total_value: ((body?.lines as Row[]) ?? []).reduce(
            (t, l) => t + Number(l.qty || 0) * Number(l.rate || 0), 0) }
      : { ...body, status: 'Active', version: 1 }
    table.rows.push(row)
    if (isDocument) PO_LINES[String(row.po_id)] = (body?.lines as Row[]) ?? []
    return { ok: true }
  }

  if (method === 'PUT' || method === 'PATCH') {
    const idKey = Object.keys(table.rows[0]).find(k => k.endsWith('_id'))!
    const row = table.rows.find(r => String(r[idKey]) === id)
    if (!row) throw new Error('No such record.')

    // Optimistic concurrency, mimicked — so the 409 message can be seen
    // without needing two people and a race.
    if (body?.version !== undefined && Number(body.version) !== Number(row.version)) {
      const err = new Error(
        `${screen} ${id} was changed by someone else while you were editing. ` +
        `Reload to see the current version.`)
      ;(err as Error & { status?: number }).status = 409
      throw err
    }
    Object.assign(row, body)
    row.version = Number(row.version) + 1
    return { ok: true }
  }

  throw new Error(`${method} is not supported.`)
}
